import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCompetition } from '../../contexts/CompetitionContext';
import axios from 'axios';
import { CheckCircle, Clock, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const stages = [
  { id: 'setup', label: 'Setup' },
  { id: 'setup_complete', label: 'Setup Done' },
  { id: 'registration', label: 'Registration' },
  { id: 'registration_closed', label: 'Reg. Closed' },
  { id: 'live', label: 'Live' },
  { id: 'complete', label: 'Complete' },
];

const AdminDashboardHome = () => {
  const { stage, competition, stats, refreshState } = useCompetition();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState({
    totalRegistered: 0,
    seatsRemaining: 0,
    eventStatus: 'Not Started',
    activeParticipants: 0,
    nextEventDate: '-'
  });

  useEffect(() => {
    refreshState();
  }, []);

  useEffect(() => {
    loadMetrics();
  }, [stage]);

  const loadMetrics = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/dashboard-metrics`, { withCredentials: true });
      setMetrics(res.data);
    } catch (e) {
      // Use stats from context as fallback
      setMetrics({
        totalRegistered: stats.totalRegistered || 0,
        seatsRemaining: stats.seatsRemaining || 0,
        eventStatus: stage === 'live' ? 'Live' : stage === 'registration' ? 'Registration Open' : stage === 'registration_closed' ? 'Registration Closed' : stage === 'setup_complete' ? 'Setup Complete' : stage === 'complete' ? 'Complete' : 'Not Started',
        activeParticipants: stats.activeParticipants || 0,
        nextEventDate: competition?.eventDate || '-'
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = () => {
    switch (stage) {
      case 'live': return 'bg-[#dcfce7] border-[#22c55e] text-[#16a34a]';
      case 'registration': return 'bg-[#fef3c7] border-[#f59e0b] text-[#d97706]';
      case 'registration_closed': return 'bg-[#eff6ff] border-[#2565eb] text-[#2565eb]';
      default: return 'bg-[#f1f5f9] border-[#e2e8f0] text-[#64748b]';
    }
  };

  const getNextAction = () => {
    switch (stage) {
      case 'setup':
        return { label: 'Go to Setup', action: () => navigate('/admin/competition') };
      case 'setup_complete':
        return { label: 'Open Registration', action: () => navigate('/admin/competition') };
      case 'registration':
        return { label: 'Manage Registration', action: () => navigate('/admin/competition') };
      case 'registration_closed':
        return { label: 'Start Competition', action: () => navigate('/admin/competition') };
      case 'live':
        return { label: 'View Live Dashboard', action: () => navigate('/admin/competition') };
      case 'complete':
        return { label: 'View Results', action: () => navigate('/admin/results') };
      default:
        return { label: 'Go to Setup', action: () => navigate('/admin/competition') };
    }
  };

  const getStageIndex = () => stages.findIndex(s => s.id === stage);
  const nextAction = getNextAction();

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-[#0f172a]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
          Dashboard
        </h1>
        <p className="text-sm text-[#64748b] mt-1">Overview of your competition status and key metrics.</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-[#2565eb] animate-spin" />
        </div>
      ) : (
        <div className="space-y-8 animate-fade-in-up">
          {/* Metric Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="metric-cards">
            <div className="card-olympiad p-4 text-center">
              <p className="text-2xl font-bold text-[#0f172a]">{metrics.totalRegistered}</p>
              <p className="text-xs font-semibold text-[#64748b] mt-1">Total Registered</p>
            </div>
            <div className="card-olympiad p-4 text-center">
              <p className="text-2xl font-bold text-[#0f172a]">{metrics.seatsRemaining}</p>
              <p className="text-xs font-semibold text-[#64748b] mt-1">Seats Remaining</p>
            </div>
            <div className={`rounded-3xl p-4 text-center border-2 ${getStatusColor()}`}>
              <p className="text-lg font-bold">{metrics.eventStatus}</p>
              <p className="text-xs font-semibold mt-1 opacity-80">Event Status</p>
            </div>
            <div className="card-olympiad p-4 text-center">
              <p className="text-2xl font-bold text-[#0f172a]">{metrics.activeParticipants}</p>
              <p className="text-xs font-semibold text-[#64748b] mt-1">Active Participants</p>
            </div>
            <div className="card-olympiad p-4 text-center col-span-2 md:col-span-1">
              <p className="text-lg font-bold text-[#0f172a]">{metrics.nextEventDate}</p>
              <p className="text-xs font-semibold text-[#64748b] mt-1">Next Event Date</p>
            </div>
          </div>

          {/* Competition Progress Stepper */}
          <div className="card-olympiad p-6" data-testid="progress-stepper">
            <h2 className="text-sm font-bold text-[#64748b] uppercase tracking-wide mb-4">Competition Progress</h2>
            <div className="flex items-center justify-between">
              {stages.map((s, i) => {
                const currentIdx = getStageIndex();
                const isComplete = i < currentIdx;
                const isCurrent = i === currentIdx;
                const isUpcoming = i > currentIdx;
                return (
                  <React.Fragment key={s.id}>
                    <div className="flex flex-col items-center gap-2">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-all ${
                        isComplete
                          ? 'bg-[#22c55e] border-[#22c55e] text-white'
                          : isCurrent
                            ? 'bg-[#2565eb] border-[#2565eb] text-white shadow-lg shadow-[#2565eb]/30'
                            : 'bg-[#f1f5f9] border-[#e2e8f0] text-[#94a3b8]'
                      }`}>
                        {isComplete ? <CheckCircle className="w-5 h-5" /> : i + 1}
                      </div>
                      <span className={`text-xs font-semibold ${
                        isCurrent ? 'text-[#2565eb]' : isComplete ? 'text-[#22c55e]' : 'text-[#94a3b8]'
                      }`}>
                        {s.label}
                      </span>
                    </div>
                    {i < stages.length - 1 && (
                      <div className={`flex-1 h-1 rounded mx-2 ${
                        i < currentIdx ? 'bg-[#22c55e]' : 'bg-[#e2e8f0]'
                      }`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* Next Action Shortcut */}
          <div className="card-olympiad p-6 text-center" data-testid="next-action">
            <p className="text-sm text-[#64748b] mb-4">What would you like to do next?</p>
            <button
              onClick={nextAction.action}
              className="btn-secondary inline-flex items-center gap-2"
              data-testid="next-action-button"
            >
              {nextAction.label}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboardHome;
